import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import FilterChip from '../ui/FilterChip';
import type { BusinessLabel } from '../../types';

interface BusinessFilterProps {
  /** Selected business, or null for "all businesses". */
  value: string | null;
  onChange: (orgId: string | null) => void;
  /** Businesses to offer. Defaults to every label in the active workspace;
   *  pass a narrower list when the page only has content for some of them. */
  orgs?: BusinessLabel[];
}

/**
 * Per-page business filter (§8). Pages default to the whole workspace and narrow
 * from here — entering a business is filtering, never a context switch, so this
 * holds page-local state and is deliberately not persisted (§10.4). Renders
 * nothing in a single-business workspace, where the choice would be theatre.
 */
const BusinessFilter: React.FC<BusinessFilterProps> = ({ value, onChange, orgs }) => {
  const { organizations } = useAuth();
  const options = orgs ?? organizations;

  if (options.length <= 1) return null;

  return (
    <FilterChip
      label={value ? options.find((o) => o.id === value)?.name ?? 'Business' : 'All businesses'}
      options={[{ id: '', label: 'All businesses' }, ...options.map((o) => ({ id: o.id, label: o.name }))]}
      selected={value ?? ''}
      onSelect={(id) => onChange(id || null)}
    />
  );
};

export default BusinessFilter;
